import axios, { AxiosError } from 'axios'

type ApiErrorBody = {
  detail?: string
  error_code?: number
  message?: string
}

const VALVE_ERROR_MESSAGES: Record<number, string> = {
  40005: 'Valve operation is not enabled for this device.',
  40008: 'The device has no valve state. Commission the valves before continuing.',
  40009: 'This valve is not fitted. Fit and commission a valve before operating it.',
  40010: 'This valve requires mains power before it can be operated.',
  40011: 'This device is not registered with Azure IoT Hub. Contact support.',
  40012: 'The device did not respond. Check that it is online and try again.',
  40013: 'Communication with the device failed. Please try again.',
}

// The API's permission denial names no resource: every 403 reads
// "User does not hold correct permission(s) to access this resource."
// When several land at once the toasts are indistinguishable without DevTools, so we
// append what was being accessed, derived from the request path.
//
// Most specific first — `/devices/{id}/valve_settings` must not match the bare `/devices`
// entry. Only paths that a user could plausibly see a denial for need an entry here; the
// fallback handles the rest.
const RESOURCE_LABELS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\/valve_settings/, 'valve settings'],
  [/\/valve_schedule/, 'valve schedules'],
  [/\/valve_status/, 'valve diagnostics'],
  [/\/valve_probe/, 'valve probe'],
  [/\/valve_recovery_policy/, 'valve recovery mode'],
  [/\/change_valve/, 'valve operation'],
  [/\/commission_valves/, 'valve commissioning'],
  [/\/set_pulses/, 'device pulse settings'],
  [/\/firmware_update/, 'device firmware'],
  [/\/rtf_token|\/real_time_flow/, 'real time flow'],
  [/\/devices\/timezones/, 'device timezones'],
  [/\/test_devices/, 'test devices'],
  [/\/staged_devices|\/device_swap/, 'staged devices'],
  [/\/devices/, 'devices'],
  [/\/customer_locations/, 'locations'],
  [/\/accounts/, 'accounts'],
  [/\/users\/me/, 'your user profile'],
  [/\/users/, 'users'],
  [/\/water_usage/, 'water usage'],
  [/\/alerts/, 'alerts'],
  [/\/tickets/, 'tickets'],
  [/\/reports/, 'reports'],
  [/\/subscription/, 'subscriptions'],
  [/\/permissions/, 'permissions'],
  [/\/roles/, 'roles'],
  [/\/groups/, 'groups'],
]

// Path segments that identify a record rather than a resource — numeric ids and UUIDs.
const ID_SEGMENT = /^\d+$|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Fallback for endpoints with no explicit label: the last segment that names something,
// with separators turned back into spaces. `/devices/42/foo_bar?x=1` -> 'foo bar'.
const deriveResourceLabel = (url: string): string | undefined => {
  const segments = url
    .split('?')[0]
    .split('/')
    .filter((segment) => segment && !ID_SEGMENT.test(segment))

  const last = segments.pop()

  return last ? last.replace(/[-_]+/g, ' ').toLowerCase() : undefined
}

export const describeApiResource = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined

  const url = error.config?.url
  if (!url) return undefined

  return RESOURCE_LABELS.find(([pattern]) => pattern.test(url))?.[1] ?? deriveResourceLabel(url)
}

export const CONNECTION_ERROR_MESSAGE =
  'Cannot reach the server. Check your internet connection — if the problem continues, contact support.'

export const TIMEOUT_ERROR_MESSAGE =
  'The server took too long to respond. Please try again.'

// A request that never got a response has no body to read a message from, so it used to
// fall all the way through to "Something went wrong" — which tells the user nothing about
// what to do. These three cases are distinguishable from the axios error code alone.
const getTransportErrorMessage = (error: AxiosError): string | undefined => {
  // A cancelled request is not a failure — an aborted signal from an unmounting component
  // or a superseded query must never surface as a toast.
  if (error.code === 'ERR_CANCELED' || axios.isCancel(error)) return undefined

  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return TIMEOUT_ERROR_MESSAGE
  }

  return CONNECTION_ERROR_MESSAGE
}

const getApiErrorMessageFromData = (data: ApiErrorBody | undefined) => {
  if (!data) return undefined

  return data.error_code
    ? VALVE_ERROR_MESSAGES[data.error_code] ?? data.message ?? data.detail
    : data.detail ?? data.message
}

export const getApiErrorMessage = (error: unknown) => {
  if (!axios.isAxiosError(error)) return undefined

  // No response means the request never reached the API — connection refused, DNS failure,
  // offline, or timeout. Handled before the body is read, since there is no body.
  if (!error.response) return getTransportErrorMessage(error)

  const message = getApiErrorMessageFromData(error.response.data as ApiErrorBody | undefined)

  // Only 403s get the suffix. Other failures either carry a specific message already (the
  // valve error codes) or name their own cause, and appending to those is noise.
  if (error.response?.status !== 403 || !message) return message

  const resource = describeApiResource(error)

  return resource ? `${message} — ${resource}` : message
}

// For a response whose body carries no message, such as FastAPI's plain-text
// 'Internal Server Error': at least say whether the server or the request failed.
export const describeResponseStatus = (status: number) =>
  status >= 500
    ? `Server error (${status})`
    : `Request failed (${status})`

// getApiErrorMessage, plus the status when a response arrived but its body named no cause.
// Kept separate so that getApiErrorMessage returning undefined still means "the API said
// nothing": callers keep their own fallback text and gain the status, e.g.
// 'Failed to commission the valves — Server error (500)'. With no response (a cancelled
// request) there is no status to add, so the fallback is returned as it is.
export function describeApiError(error: unknown, fallback: string): string
export function describeApiError(error: unknown): string | undefined
export function describeApiError(error: unknown, fallback?: string) {
  const message = getApiErrorMessage(error)
  if (message !== undefined) return message

  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  if (status === undefined) return fallback

  const statusText = describeResponseStatus(status)

  return fallback ? `${fallback.replace(/\.$/, '')} — ${statusText}` : statusText
}
